import { fmtNum } from './charts';

export function normalizeMaturityWeights(draft) {
  const raw = {
    done: draft.maturityWeightDone ?? 0.3,
    rating: draft.maturityWeightRating ?? 0.5,
    recent: draft.maturityWeightRecent ?? 0.2,
  };
  const sum = raw.done + raw.rating + raw.recent;
  if (sum <= 0) return { done: 0, rating: 0, recent: 0, allOff: true };
  return {
    done: raw.done / sum,
    rating: raw.rating / sum,
    recent: raw.recent / sum,
    allOff: false,
  };
}

export function fmtMaturityWeightBar(weights) {
  return [
    { key: 'done', pct: weights.done, color: '#26B99A' },
    { key: 'rating', pct: weights.rating, color: '#3498DB' },
    { key: 'recent', pct: weights.recent, color: '#E67E22' },
  ].filter((s) => s.pct > 0);
}

export function normalizeWeights(draft) {
  const raw = {
    wE: draft.toggleE !== false ? (draft.wE ?? 0.35) : 0,
    wR: draft.toggleR !== false ? (draft.wR ?? 0.35) : 0,
    wS: draft.toggleS !== false ? (draft.wS ?? 0.3) : 0,
  };
  const sum = raw.wE + raw.wR + raw.wS;
  if (sum <= 0) return { wE: 0, wR: 0, wS: 0, allOff: true };
  return {
    wE: raw.wE / sum,
    wR: raw.wR / sum,
    wS: raw.wS / sum,
    allOff: false,
  };
}

export function expertiseQDelta(draft) {
  const weights = normalizeWeights(draft);
  const eTop5 = draft.eTop5 ?? 1.0;
  const eDeclared = draft.eDeclared ?? 0.35;
  return weights.wE * (eTop5 - eDeclared);
}

export function speedTable(draft) {
  const tau = draft.tau ?? 8;
  const business = draft.toggleBusinessHoursSpeed !== false;
  const hours = business ? [1, 4, 8, 24] : [1, 4, 12, 24];
  return hours.map((h) => ({
    hours: h,
    S: Math.exp(-h / tau),
  }));
}

export function ratingForSingle(draft, rating = 4.8, n = 1) {
  const mR = draft.m_R ?? 5;
  const r0 = draft.r0 ?? 4.0;
  const rStar = (n * rating + mR * r0) / (n + mR);
  const R = Math.min(1, Math.max(0, (rStar - 1) / 4));
  return { R, rStar };
}

export function dAtBreachRates(draft) {
  const kSla = draft.k_sla ?? 1.5;
  const dFloor = draft.D_floor ?? 0.4;
  const rates = [0.1, 0.25, 0.5];
  return rates.map((v) => ({
    v,
    D: Math.max(dFloor, 1 - kSla * v),
  }));
}

export function softmaxRatio(sigmaA, sigmaB, T) {
  const temp = T > 0 ? T : 0.01;
  const expA = Math.exp((sigmaA - Math.max(sigmaA, sigmaB)) / temp);
  const expB = Math.exp((sigmaB - Math.max(sigmaA, sigmaB)) / temp);
  return expB > 0 ? expA / expB : 0;
}

export function weightWarnings(draft) {
  const warnings = [];
  const pairs = [
    ['wE', 'toggleE', 'E'],
    ['wR', 'toggleR', 'R'],
    ['wS', 'toggleS', 'S'],
  ];
  pairs.forEach(([wKey, toggleKey, label]) => {
    if (draft[toggleKey] !== false && (draft[wKey] ?? 0) === 0) {
      warnings.push({ key: wKey, label });
    }
  });
  return warnings;
}

export function fmtWeightBar(weights) {
  return [
    { key: 'wE', pct: weights.wE, color: '#26B99A' },
    { key: 'wR', pct: weights.wR, color: '#3498DB' },
    { key: 'wS', pct: weights.wS, color: '#9B59B6' },
  ].filter((s) => s.pct > 0);
}

export { fmtNum };
